import React, { useState, Fragment } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';
import { formatMoney, formatUF } from '@/utils/format';
import Text from '../../designSystem/Text';
import Modal from '../../designSystem/Modal';
import Icon from '../../designSystem/Icon';

import CircleLoader from '../CircleLoader';

import styles from './ReviewInfoCapsule.module.scss';

import { UF as UFType } from '../../types/uf';
import componentsId from '../../utils/componentsId';

import * as plansSelectors from '../../redux/plans/selectors';
import * as wizardSelectors from '../../redux/wizard/selectors';
import * as ufSelectors from '../../redux/uf/selectors';
import {
  variantsStepTexts,
  wizardReviewerTexts,
  coveragesStepTexts as coverageText,
  cyberReviewText,
} from '../../utils/generalTexts';
import CyberReview from '../CyberReview';
import { useCyberDates } from '@/src/customHooks';

type ReviewInfoCapsuleT = {
  customClass?: string;
};

function ReviewInfoCapsule({ customClass }: ReviewInfoCapsuleT) {
  const [isDetailsShowed, setIsDetailsShowed] = useState(false);

  const reviewInfoCapsuleClass = classNames(styles.review_content, {
    [`${customClass}`]: customClass,
  });

  const toggleDetails = () => {
    const wizardFooter = document.getElementsByClassName(styles.wizard_footer)[0];

    if (!isDetailsShowed) {
      wizardFooter?.classList.add(styles.show);
    } else {
      wizardFooter?.classList.remove(styles.show);
    }

    setIsDetailsShowed(!isDetailsShowed);
  };

  const UF: UFType = useSelector(ufSelectors.getUF);
  const workers = useSelector(wizardSelectors.getWorkersSelected);
  const variantSelected = useSelector(wizardSelectors.getVariantSelected);
  const plan = useSelector(plansSelectors.getPreSelectedPlan());
  const selectedCoverages = useSelector(wizardSelectors.getSelectedCoverages);
  const dentalPlan = useSelector(plansSelectors.getDentalPlan());
  const catastrophicPlan = useSelector(plansSelectors.getCatastrophicPlan());
  const variant = useSelector(plansSelectors.getVariantById(variantSelected?.variantId));

  const totalUFCompany = useSelector(wizardSelectors.getTotalCompany);
  const totalUFByPlan = useSelector(wizardSelectors.getTotalByPlan);
  const totalUFVariants = useSelector(wizardSelectors.getTotalVariants);

  const planSubTotal = `${formatMoney(totalUFByPlan * UF.value)}<span>${formatUF(
    totalUFByPlan
  )}</span>`;
  const variantsSubTotal = `${formatMoney(totalUFVariants * UF.value)}${
    !!totalUFVariants ? `<span>${formatUF(totalUFVariants)}</span>` : ''
  }`;
  const placeholderUF = `<span>0 UF</span>`;
  const totalCompany = `${formatMoney(totalUFCompany * UF.value)}${
    totalUFCompany > 0 ? `<span>${formatUF(totalUFCompany)}</span>` : placeholderUF
  }`;
  const dentalSubTotal =
    !!dentalPlan &&
    `${formatMoney(dentalPlan.totalCost * UF.value)}
    <span>${formatUF(dentalPlan.totalCost)}</span>`;
  const catastrophicSubTotal =
    !!catastrophicPlan &&
    `${formatMoney(catastrophicPlan.totalCost * UF.value)}
    <span>${formatUF(catastrophicPlan.totalCost)}</span>`;

  const wizardLoading = useSelector(wizardSelectors.getLoading);

  const promoCyberData = useCyberDates(new Date());
  const { cyberDay } = promoCyberData;

  const showCyberReview = cyberDay !== null;

  const reviewModalClass = classNames(styles.review_modal, {
    [styles.review_modal_cyber]: showCyberReview,
  });

  return (
    <Fragment>
      <Modal
        isOpen={isDetailsShowed}
        close={toggleDetails}
        closeOnOverlay
        type="half_modal"
        modalClass={reviewModalClass}
        overlayClass={styles.review_modal_overlay}
        closeOnIcon={false}
        fromTop
      >
        <div className={styles.review_modal_container}>
          <div className={styles.review_modal_header}>
            <Text text={wizardReviewerTexts.modal.title} customClass={styles.review_modal_title} />
          </div>

          {showCyberReview && !!plan && (
            <CyberReview
              discount={cyberReviewText.discount(plan.base)}
              className={styles.review_estimate_cyber}
            />
          )}

          <div className={styles.review_modal_content}>
            {!!workers && (
              <div className={styles.review_modal_step_container}>
                <Text
                  customClass={styles.review_modal_step}
                  text={wizardReviewerTexts.modal.workers}
                />
                <Text customClass={styles.review_modal_value} text={workers} />
              </div>
            )}

            {!!plan && (
              <div className={styles.review_modal_step_container}>
                <Text
                  customClass={styles.review_modal_step}
                  text={coverageText.life.description(plan.description)}
                />
                {!!plan.price.uf && (
                  <Text text={planSubTotal} customClass={styles.review_modal_value} />
                )}
              </div>
            )}

            {!!selectedCoverages.dental && (
              <div className={styles.review_modal_step_container}>
                <Text
                  customClass={styles.review_modal_step}
                  text={coverageText.dental.description}
                />
                {!!plan?.price.uf && (
                  <Text text={dentalSubTotal} customClass={styles.review_modal_value} />
                )}
              </div>
            )}

            {!!selectedCoverages.catastrophic && (
              <div className={styles.review_modal_step_container}>
                <Text
                  customClass={styles.review_modal_step}
                  text={coverageText.catastrophic.description}
                />
                {!!plan?.price.uf && (
                  <Text text={catastrophicSubTotal} customClass={styles.review_modal_value} />
                )}
              </div>
            )}

            {!!variantSelected && (
              <div className={styles.review_modal_step_container}>
                <Text
                  customClass={styles.review_modal_step}
                  text={variant?.description || variantsStepTexts.distributionCard.review}
                />
                <Text text={variantsSubTotal} customClass={styles.review_modal_value} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <div
        id={componentsId.wizardResumeCapsule}
        className={reviewInfoCapsuleClass}
        onClick={toggleDetails}
      >
        <div className={styles.review_amount_container}>
          <Text customClass={styles.review_total_amount} text={totalCompany} />
          {wizardLoading ? (
            <CircleLoader size="small" customClass={styles.review_loader} />
          ) : (
            <Icon
              name={isDetailsShowed ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              size="medium"
              customClass={styles.review_total_icon}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default ReviewInfoCapsule;
